import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CaseInformation, CaseInformationService, OpposingParty, SharedPreReadNote, humanizeToken } from 'src/app/services/case-information.service';
import { CaseNotesService, Note } from 'src/app/services/case-notes.service';
import { NoteDialogComponent, NoteDialogData } from '../note-dialog/note-dialog.component';
import { ShareNoteDialogComponent } from '../share-note-dialog/share-note-dialog.component';
import { ConfirmDialogComponent } from 'src/app/confirm-dialog/confirm-dialog.component';
import { DocumentPreviewComponent } from '../document-preview/document-preview.component';
import { HttpService } from 'src/app/services/http.service';
import { URLUtils } from 'src/app/urlUtils';

@Component({
    selector: 'app-case-detail',
    templateUrl: './case-detail.component.html',
    styleUrls: ['./case-detail.component.scss'],
    standalone: false
})
export class CaseDetailComponent implements OnInit, OnDestroy {

    relId: string = '';
    caseId: string = '';
    caseData: CaseInformation | undefined;
    loading: boolean = true;
    activeTab: 'overview' | 'documents' | 'notes' = 'overview';

    notes: Note[] = [];
    notesLoading: boolean = true;
    private notesLoaded: boolean = false;

    expandedPartyIndex: number | null = null;

    singlePartyCollapsed: boolean = false;

    private readonly LONG_TEXT_THRESHOLD = 220;
    private expandedFields: { [key: string]: boolean } = {};

    private readonly NOTE_PREVIEW_THRESHOLD = 140;

    private pendingDocumentOperations = new Map<string, boolean>();

    private routeParamsSub?: Subscription;

    private loadedCaseId: string | null = null;

    // Cases Shared list context
    private listClientName?: string;
    private listClientType?: string;
    private listPreReadIds?: string[];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private caseInformationService: CaseInformationService,
        private caseNotesService: CaseNotesService,
        private dialog: MatDialog,
        private toastr: ToastrService,
        private httpService: HttpService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.relId = this.route.snapshot.paramMap.get('relId') || '';

        this.routeParamsSub = this.route.paramMap.subscribe(params => {
            const caseId = params.get('caseId') || '';
            this.caseId = caseId;
            this.handleCaseIdChange(caseId);
        });
    }

    ngOnDestroy(): void {
        this.routeParamsSub?.unsubscribe();
        // Clear notes cache when leaving the component
        if (this.caseId) {
            this.caseNotesService.clearCache(this.caseId);
        }
    }

    private handleCaseIdChange(caseId: string): void {
        if (!caseId) {
            this.loadedCaseId = null;
            this.loading = false;
            this.notesLoading = false;
            this.caseData = undefined;
            this.notes = [];
            return;
        }

        if (this.loadedCaseId === caseId) {
            return;
        }
        this.loadedCaseId = caseId;
        this.notesLoaded = false;

        // Cases Shared list context
        const state: any = history.state;
        if (state?.clientName !== undefined || state?.preReadIds !== undefined) {
            this.listClientName = state.clientName;
            this.listClientType = state.clientType;
            this.listPreReadIds = state.preReadIds;
        }

        // Reuse case from state if available
        if (state?.case && (state.case.preReadId === caseId || state.case.caseId === caseId)) {
            this.caseData = state.case;
            this.loading = false;
            this.singlePartyCollapsed = false;
            this.initNotesFromCase(this.caseData);
        } else {
            this.loadCaseDetails();
        }
    }

    private loadCaseDetails(): void {
        this.loading = true;
        this.caseData = undefined;
        this.caseInformationService.getSharedCaseInformation(this.caseId).subscribe({
            next: (data) => {
                console.log('Fetched case information:', data);
                this.caseData = this.caseInformationService.mapSharedPreReadToCaseInformation(this.caseId, data);
                this.caseData.updatedAt = data.last_updated; //getting sharedon in IST
                this.loading = false;
                this.singlePartyCollapsed = false;
                this.initNotesFromCase(this.caseData);
                this.cdr.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.caseData = undefined;
                this.notesLoading = false;
                this.toastr.error('Unable to load case information. Please try again.');
                this.cdr.detectChanges();
            }
        });
    }

    // Seeds CaseNotesService with initial notes
    private initNotesFromCase(caseData: CaseInformation | undefined): void {
        if (this.notesLoaded) {
            return;
        }
        
        const seedNotes = (caseData?.notes || []).map(n => this.mapApiNote(n));
        this.caseNotesService.seedNotes(this.caseId, seedNotes);
        this.loadNotes();
    }

    private mapApiNote(raw: SharedPreReadNote): Note {
    // Use bracket notation for index signature properties
    const created = raw['createdAt'] || raw['created_at'];
    const noteId = raw['id'] || raw['note_id'];
    const content = raw['content'] || raw['note'] || '';
    const author = raw['author'] || 'Unknown';
    const shared = raw['shared'] || false;
    
    return {
        id: noteId || this.generateFallbackNoteId(),
        caseId: this.caseId,
        content: content,
        createdAt: created ? new Date(created) : new Date(),
        author: author,
        shared: shared,
        note_id: noteId,
        note: content,
        created_at: raw['createdAt'] || raw['created_at']
    };
}

    private generateFallbackNoteId(): string {
        return 'NOTE' + Date.now().toString(36).toUpperCase() + Math.floor(Math.random() * 1000);
    }

    private loadNotes(): void {
    this.notesLoading = true;
    this.caseNotesService.getNotes(this.caseId).subscribe({
        next: (notes) => {
            this.notes = notes;
            this.notesLoading = false;
            this.notesLoaded = true;
            this.cdr.detectChanges();
        },
        error: (error) => {
            this.notes = [];
            this.notesLoading = false;
            this.notesLoaded = true;
            this.toastr.error('Unable to load notes. Please refresh the page.');
            this.cdr.detectChanges();
        }
    });
}
    // Generic "does this field have a real value" check
    hasValue(val: any): boolean {
        if (val === null || val === undefined) { return false; }
        if (typeof val === 'string') { return val.trim().length > 0; }
        if (Array.isArray(val)) { return val.length > 0; }
        return true;
    }

    // Get document icon based on file type
    getDocIcon(fileType?: string): string {
        if (!fileType) return 'fa-file-o';
        const type = fileType.toLowerCase();
        if (['pdf'].includes(type)) return 'fa-file-pdf-o';
        if (['doc', 'docx'].includes(type)) return 'fa-file-word-o';
        if (['xls', 'xlsx'].includes(type)) return 'fa-file-excel-o';
        if (['zip', 'rar', '7z'].includes(type)) return 'fa-file-archive-o';
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(type)) return 'fa-file-image-o';
        if (['txt'].includes(type)) return 'fa-file-text-o';
        return 'fa-file-o';
    }

    // Overview: Opposing Parties
    togglePartyExpand(index: number): void {
        this.expandedPartyIndex = this.expandedPartyIndex === index ? null : index;
    }

    isPartyExpanded(index: number): boolean {
        if (this.caseData?.opposingParties && this.caseData.opposingParties.length === 1) {
            return !this.singlePartyCollapsed;
        }
        return this.expandedPartyIndex === index;
    }

    toggleSingleParty(): void {
        this.singlePartyCollapsed = !this.singlePartyCollapsed;
    }

    getPartyName(party: string | OpposingParty): string {
        return typeof party === 'string' ? party : party.name;
    }

    getPartyDetail(party: string | OpposingParty, field: 'phone' | 'role' | 'email' | 'address'): string | undefined {
        return typeof party === 'string' ? undefined : party[field];
    }

    hasPartyDetails(party: string | OpposingParty): boolean {
        if (typeof party === 'string') { return false; }
        return !!(party.phone || party.role || party.email || party.address);
    }

    // Overview: Read more / Read less
    isLongText(text?: string): boolean {
        return !!text && text.trim().length > this.LONG_TEXT_THRESHOLD;
    }

    isFieldExpanded(key: string): boolean {
        return !!this.expandedFields[key];
    }

    toggleFieldExpanded(key: string): void {
        this.expandedFields[key] = !this.expandedFields[key];
    }

    get documentCount(): number {
        return this.caseData?.documentsCount ?? (this.caseData?.supportingDocuments?.length || 0);
    }

    get noteCount(): number {
        return this.notes.length;
    }

    setTab(tab: 'overview' | 'documents' | 'notes'): void {
        this.activeTab = tab;
        const panel = document.querySelector('.tab-panel');
        if (panel) {
            panel.scrollTop = 0;
        }
        // Refresh notes if switching to notes tab and notes haven't been loaded yet
        if (tab === 'notes' && !this.notesLoaded && this.caseId) {
            this.loadNotes();
        }
    }

    statusLabel(status?: string): string {
        if (status === 'completed') { return 'Completed'; }
        if (status === 'in_progress') { return 'In Progress'; }
        if (status === 'draft') { return 'Draft'; }
        return humanizeToken(status);
    }

    backToCaseList(): void {
        this.router.navigate(['/relationship/view/individuals', this.relId, 'cases'], {
            state: {
                clientName: this.listClientName,
                clientType: this.listClientType,
                preReadIds: this.listPreReadIds
            }
        });
    }

    /**
     * View a document - opens the production-ready document preview
     */
    viewDocument(doc: any): void {
        if (!doc || !doc.id) {
            this.toastr.error('Document information is missing.');
            return;
        }

        const operationKey = `view-${doc.id}`;
        if (this.pendingDocumentOperations.get(operationKey)) {
            return; // Prevent duplicate requests
        }
        this.pendingDocumentOperations.set(operationKey, true);

        const docName = doc.name || 'Document';
        
        // Show loading toast
        this.toastr.info('Loading document...', '', { timeOut: 3000, disableTimeOut: true });

        // Call the document API to get the URL
        const url = URLUtils.getDocumentAction(doc.id, 'view');
        
        this.httpService.sendGetRequest(url).subscribe({
            next: (response: any) => {
                this.pendingDocumentOperations.delete(operationKey);
                
                // Clear loading toast
                this.toastr.clear();

                let documentUrl: string | null = null;
                
                // Parse the response to get the URL
                if (response && response.data && response.data.url) {
                    documentUrl = response.data.url;
                } else if (response && response.url) {
                    documentUrl = response.url;
                } else if (typeof response === 'string' && response.startsWith('http')) {
                    documentUrl = response;
                }

                if (!documentUrl) {
                    this.toastr.error('Could not retrieve document URL. Please try again.');
                    return;
                }

                // Open the document preview dialog
                this.dialog.open(DocumentPreviewComponent, {
                    width: '95vw',
                    maxWidth: '100vw',
                    height: '95vh',
                    maxHeight: '100vh',
                    panelClass: 'document-preview-dialog',
                    disableClose: false,
                    hasBackdrop: true,
                    backdropClass: 'document-preview-backdrop',
                    data: {
                        documentUrl: documentUrl,
                        documentName: docName,
                        fileType: doc.fileType || this.getFileExtension(docName),
                        documentId: doc.id
                    }
                });
            },
            error: (error) => {
                this.pendingDocumentOperations.delete(operationKey);
                this.toastr.clear();
                this.toastr.error('Unable to load document. Please try again later.');
                console.error('Document view error:', error);
            }
        });
    }

    /**
     * Download a document
     */
    downloadDocument(doc: any): void {
        if (!doc || !doc.id) {
            this.toastr.error('Document information is missing.');
            return;
        }

        const operationKey = `download-${doc.id}`;
        if (this.pendingDocumentOperations.get(operationKey)) {
            return; // Prevent duplicate requests
        }
        this.pendingDocumentOperations.set(operationKey, true);

        const docName = doc.name || 'document.pdf';
        this.toastr.info('Preparing download...', '', { timeOut: 3000, disableTimeOut: true });

        // Call the document API to get the download URL
        const url = URLUtils.getDocumentAction(doc.id, 'download');
        
        this.httpService.sendGetRequest(url).subscribe({
            next: (response: any) => {
                this.pendingDocumentOperations.delete(operationKey);
                this.toastr.clear();

                let downloadUrl: string | null = null;
                
                if (response && response.data && response.data.url) {
                    downloadUrl = response.data.url;
                } else if (response && response.url) {
                    downloadUrl = response.url;
                } else if (typeof response === 'string' && response.startsWith('http')) {
                    downloadUrl = response;
                }

                if (!downloadUrl) {
                    this.toastr.error('Could not retrieve download URL. Please try again.');
                    return;
                }

                // Trigger download
                this.triggerDownload(downloadUrl, docName);
            },
            error: (error) => {
                this.pendingDocumentOperations.delete(operationKey);
                this.toastr.clear();
                this.toastr.error('Unable to download document. Please try again later.');
                console.error('Document download error:', error);
            }
        });
    }

    /**
     * Trigger a file download from a URL
     */
    private triggerDownload(url: string, fileName: string): void {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.toastr.success('Downloaded Successfully');
    }

    /**
     * Get file extension from a filename
     */
    private getFileExtension(fileName: string): string {
        if (!fileName) return '';
        const parts = fileName.split('.');
        return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
    }

    // Notes

    openAddNotes(): void {
        const data: NoteDialogData = { mode: 'add', caseTitle: this.caseData?.issueTitle };
        const dialogRef = this.dialog.open(NoteDialogComponent, {
            width: '520px',
            maxWidth: '95vw',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'backdropBackground',
            data
        });

        dialogRef.afterClosed().subscribe((result: string[] | undefined) => {
            if (!result || !result.length) {
                return;
            }
            
            // Show loading state in the UI
            this.notesLoading = true;
            
            // Use the service to add notes (handles multiple notes via sequential calls)
            this.caseNotesService.addNotes(this.caseId, result).subscribe({
                next: (notes) => {
                    this.notes = notes;
                    this.notesLoading = false;
                    this.notesLoaded = true;
                    this.activeTab = 'notes';
                    this.toastr.success(`Note${result.length > 1 ? 's' : ''} added successfully.`);
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.notesLoading = false;
                    // Reload notes from API to get the current state
                    this.loadNotes();
                    // Show the actual error message
                    const errorMessage = error.message || error.toString() || 'Failed to add notes. Please try again.';
                    this.toastr.error(errorMessage);
                    this.cdr.detectChanges();
                }
            });
        });
    }

    isNoteLong(content: string): boolean {
        return !!content && content.trim().length > this.NOTE_PREVIEW_THRESHOLD;
    }

    openViewNote(note: Note): void {
        const data: NoteDialogData = { mode: 'view', caseTitle: this.caseData?.issueTitle, existingContent: note.content };
        this.dialog.open(NoteDialogComponent, {
            width: '520px',
            maxWidth: '95vw',
            disableClose: false,
            hasBackdrop: true,
            backdropClass: 'backdropBackground',
            data
        });
    }

    openEditNote(note: Note): void {
        if (!note.hasPersistedId) {
            this.toastr.error('This note can\'t be edited right now — it\'s missing an identifier from the server. Try refreshing the page.');
            return;
        }

        const data: NoteDialogData = { mode: 'edit', existingContent: note.content };
        const dialogRef = this.dialog.open(NoteDialogComponent, {
            width: '520px',
            maxWidth: '95vw',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'backdropBackground',
            data
        });

        dialogRef.afterClosed().subscribe((result: string | undefined) => {
            if (!result) {
                return;
            }
            
            this.notesLoading = true;
            
            const noteId = note.note_id || note.id;
            this.caseNotesService.updateNote(this.caseId, noteId, result).subscribe({
                next: (notes) => {
                    this.notes = notes;
                    this.notesLoading = false;
                    this.notesLoaded = true;
                    this.activeTab = 'notes';
                    this.toastr.success('Note updated successfully.');
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.notesLoading = false;
                    this.loadNotes();
                    this.toastr.error(error.message || 'Failed to update note. Please try again.');
                    this.cdr.detectChanges();
                }
            });
        });
    }

    openDeleteNote(note: Note): void {
        // Same backend limitation as openEditNote — see comment there.
        if (!note.hasPersistedId) {
            this.toastr.error('This note can\'t be deleted right now — it\'s missing an identifier from the server. Try refreshing the page.');
            return;
        }

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'backdropBackground',
            data: { title: 'Delete Note', message: 'Are you sure you want to delete this note?' }
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) {
                return;
            }
            
            this.notesLoading = true;
            
            const noteId = note.note_id || note.id;
            this.caseNotesService.deleteNote(this.caseId, noteId).subscribe({
                next: (notes) => {
                    this.notes = notes;
                    this.notesLoading = false;
                    this.notesLoaded = true;
                    this.activeTab = 'notes';
                    this.toastr.success('Note deleted successfully.');
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.notesLoading = false;
                    this.loadNotes();
                    this.toastr.error(error.message || 'Failed to delete note. Please try again.');
                    this.cdr.detectChanges();
                }
            });
        });
    }

    openShareNote(note: Note): void {
        const dialogRef = this.dialog.open(ShareNoteDialogComponent, {
            width: '440px',
            maxWidth: '95vw',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'backdropBackground',
            data: { note, clientName: this.caseData?.sharedByName }
        });

        dialogRef.afterClosed().subscribe((shared: boolean | undefined) => {
            if (shared) {
                const noteId = note.note_id || note.id;
                this.caseNotesService.shareNote(this.caseId, noteId, this.caseData?.sharedByName || '').subscribe({
                    next: () => {
                        note.shared = true;
                        this.toastr.success('Note shared successfully.');
                    },
                    error: () => {
                        this.toastr.error('Failed to share note. Please try again.');
                    }
                });
            }
        });
    }
}